import React, { useMemo } from 'react';

import AlternatingRowDataTable from '../../../common/DataTable/AlternatingRowDataTable';
import Label from '../../../common/Labels/Label';

import PipelineStatusWithPopover from '../../../pipelines/PipelineStatusBadge/PipelineStatusWithPopover';
import { averageFormatter, percentFormatter, twoDigitDecimalFormatter } from '../../../../utils/formatters';

import { formatMemoryUnitsAsGB, formatMemoryUnitsAsGi } from '../../../../utils/prometheus_utils';

const transformPipelinesToStatsTable = (rawPipelines: any[] = []) => {
  return rawPipelines.map((rawPipeline) => {
    return ({
      ...rawPipeline,
      id: rawPipeline?.pipeline?.id,
      name: rawPipeline?.pipeline?.name || '',
      deploymentId: rawPipeline?.deployment?.id,
      workspaceName: rawPipeline?.workspace?.name,
      workspace: {
        workspace_id: rawPipeline?.workspace?.id,
        name: rawPipeline?.workspace?.name,
        created_by: rawPipeline?.workspace?.created_by
      },
      arch: rawPipeline?.deployment?.engine_config?.engine?.arch || '',
      accel: rawPipeline?.deployment?.engine_config?.engine?.accel || '',
      deployLabel: rawPipeline?.deployment?.engine_config?.engine?.node_selector || '',
      status: rawPipeline?.deployment?.deployed ? 'Active' : 'Inactive',
      cpuUsed: rawPipeline?.metrics?.cpuUsed,
      cpuReq: rawPipeline?.metrics?.cpuReq,
      cpuPercent: rawPipeline?.metrics?.cpuPercent,
      gpuUsed: rawPipeline?.metrics?.gpuUsed,
      gpuReq: rawPipeline?.metrics?.gpuReq,
      gpuPercent: rawPipeline?.metrics?.gpuPercent,
      memUsed: rawPipeline?.metrics?.memUsed,
      memReq: rawPipeline?.metrics?.memReq,
      memPercent: rawPipeline?.metrics?.memPercent,
      logSize: rawPipeline?.metrics?.logSize,
      topicName: rawPipeline?.plateau_topic,
    })
  })
}

const truncateText = (inputText, truncateAt = 8) => {
  if (inputText.length < truncateAt + 1) {
    return inputText;
  }
  return `${inputText.substring(0, truncateAt)}...`;
}

const NameCell = ({ value }) => {
  const truncatedName = truncateText(value || '--', 30);

  return (
    <div className="flex flex-col p-2">
      <Label
        className="break-words"
        size="lg"
      >
        {truncatedName}
      </Label>
    </div>
  )
}

const LabelCell = ({ value }) => <div className="flex"><Label>{ value }</Label></div>

const CenteredLabelCell = ({ value }) => <div className="flex items-center justify-center"><Label>{ value }</Label></div>

const OneDigitFloatCell = ({ value }) => <div className="flex items-center justify-center"><Label>{ value !== null && value !== undefined ? averageFormatter.format(value) : '--' }</Label></div>

const TwoDigitFloatCell = ({ value }) => <div className="flex items-center justify-center"><Label>{ value !== null && value !== undefined ? twoDigitDecimalFormatter.format(value) : '--' }</Label></div>

const PercentCell = ({ value }) => <div className="flex items-center justify-center"><Label>{ value !== null && value !== undefined ? percentFormatter.format(value) : '--' }</Label></div>

const StatusCell = ({ row, value }) => <div className="flex items-center justify-center"><PipelineStatusWithPopover deploymentUpdatedAt={row?.original?.deployment_updated_at} deployed={row?.original?.deployed} status={value} /></div>

// Storage Calculation/Units
const StorageSizeCell = ({ value }) => {
  const storageLabel = (value === null || value === undefined) ? '--' : formatMemoryUnitsAsGB(value);
  return (
    <div className="flex flex-col items-center justify-center">
      <Label>{storageLabel}</Label>
    </div>
  )
}

const WorkspaceCell = ({ value }) => {
  const truncatedName = truncateText(value || '--', 30);
  return (
    <div className="flex flex-col">
      <Label className="break-words">{truncatedName}</Label>
    </div>
  )
}

const UsedMemCell = ({ value }) => {
  const displayValue = (value === undefined || value === null) ? null : formatMemoryUnitsAsGi(value);
  return (
    <TwoDigitFloatCell value={displayValue}/>
  )
}

const ReqMemCell = ({ value }) => {
  const displayValue = (value === undefined || value === null) ? null : formatMemoryUnitsAsGi(value);
  return (
    <OneDigitFloatCell value={displayValue}/>
  )
}

const DeploymentLabelCell = ({ value }) => {
  return (
    <CenteredLabelCell value={value?.length ? value : 'None'} />
  )
}

export const PipelineStatsBreakdownTable = ({
  pipelines = [],
  selectedPage = 1,
  resultsPerPage = 10,
}) => {
  const data = useMemo(() => transformPipelinesToStatsTable(pipelines), [pipelines]);

  const columns = useMemo(() => [
    {
      id: 'group1',
      Header: '',
      disableSortBy: true,
      centerColumn: true,
      columns: [
        {
          Header: 'Name',
          accessor: 'name',
          Cell: NameCell,
          width: 164,
        },
        {
          Header: 'Workspace',
          accessor: 'workspaceName',
          Cell: WorkspaceCell,
          width: 164,
        },
      ]
    },
    {
      Header: 'Avg. CPU (vCPUs)',
      disableSortBy: true,
      centerColumn: true,
      width: 180,
      columns: [
        {
          id: 'cpuUsed',
          Header: 'Used',
          accessor: 'cpuUsed',
          Cell: TwoDigitFloatCell,
          width: 60,
          centerColumn: true,
        },
        {
          id: 'cpuReq',
          Header: 'Req',
          accessor: 'cpuReq',
          Cell: OneDigitFloatCell,
          width: 60,
          centerColumn: true,
        },
        {
          id: 'cpuPercent',
          Header: '%',
          accessor: 'cpuPercent',
          Cell: PercentCell,
          width: 60,
          centerColumn: true,
        },
      ]
    },
    {
      Header: 'Avg. GPU',
      disableSortBy: true,
      centerColumn: true,
      width: 180,
      columns: [
        {
          id: 'gpuUsed',
          Header: 'Used',
          accessor: 'gpuUsed',
          Cell: TwoDigitFloatCell,
          width: 60,
          centerColumn: true,
        },
        {
          id: 'gpuReq',
          Header: 'Req',
          accessor: 'gpuReq',
          Cell: OneDigitFloatCell,
          width: 60,
          centerColumn: true,
        },
        {
          id: 'gpuPercent',
          Header: '%',
          accessor: 'gpuPercent',
          Cell: PercentCell,
          width: 60,
          centerColumn: true,
        },
      ]
    },
    {
      Header: 'Avg. Memory (Gi)',
      disableSortBy: true,
      centerColumn: true,
      columns: [
        {
          id: 'memUsed',
          Header: 'Used',
          accessor: 'memUsed',
          Cell: UsedMemCell,
          width: 80,
          centerColumn: true,
        },
        {
          id: 'memReq',
          Header: 'Req',
          accessor: 'memReq',
          Cell: ReqMemCell,
          width: 80,
          centerColumn: true,
        },
        {
          id: 'memPercent',
          Header: '%',
          accessor: 'memPercent',
          Cell: PercentCell,
          width: 80,
          centerColumn: true,
        },
      ]
    },
    {
      id: 'group2',
      Header: '',
      disableSortBy: true,
      centerColumn: true,
      columns: [
        {
          Header: 'Arch',
          accessor: 'arch',
          Cell: LabelCell,
          width: 80,
        },
        {
          Header: 'Accel',
          accessor: 'accel',
          Cell: LabelCell,
          width: 96,
        },
        {
          Header: 'Deployment Label',
          accessor: 'deployLabel',
          Cell: DeploymentLabelCell,
          width: 196,
        },
        {
          Header: 'Log Size',
          accessor: 'logSize',
          Cell: StorageSizeCell,
          width: 128,
          centerColumn: true,
        }
      ]
    },
  ], []);

  const defaultColumn = React.useMemo(
    () => ({
      width: 140,
    }),
    []
  )

  const defaultSort = {
    id: 'name',
    desc: false
  }

  // NOTE - When grouping columns, we need to use centerColumns
  return (
    <>
      <AlternatingRowDataTable
        id="pipelinestats-data-table"
        columns={columns}
        data={data}
        defaultColumn={defaultColumn}
        // @ts-ignore
        defaultSort={defaultSort}
        height={640}
        pagination={{
          resultsPerPage,
          selectedPage
        }}
        centerColumns
      />
    </>
  )
}

export default PipelineStatsBreakdownTable;
